'use client';

import { useState } from 'react';
import { DISTANCE_UNITS } from '@config/constants';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '@hooks/useScreenWidth';
import { getPlaceUrl } from '@domain/routes/urls';
import { Button } from '../Clickables/Buttons/Button';
import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { PlaceEntry } from './components/PlaceEntry';

export interface IPlace {
  name: string;
  distance?: number;
  distanceUnit?: (typeof DISTANCE_UNITS)[keyof typeof DISTANCE_UNITS];
  slug: string | null;
  country: {
    id: string;
    countryName: string;
  };
  region: {
    id: string | null;
    name: string | null;
  };
}

interface IProps {
  id?: string;
  places: IPlace[];
  title: string;
  showLocationLinks?: boolean;
}

const SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE = 6;
const SHOW_NUMBER_OF_NEARBY_PLACES_TABLET = 9;

/**
 * PlacesSection component displays a list of places optionally with their distances from a main Location (lng/lat).
 * It shows a limited number of places on mobile and tablet screens, with an option to show more.
 *
 * @param {string} [id='nearby-places'] - The id of the container element.
 * @param {DISTANCE_UNITS} [distanceUnit=DISTANCE_UNITS.MILES] - The unit of distance to display.
 * @param {IPlace[]} places - An array places to display.
 * @param {string} [title='Cities nearby'] - The title of the section.
 *
 * @returns {JSX.Element} The PlacesSection component.
 */
export const PlacesSection = ({
  id = 'places-section',
  places,
  title,
  showLocationLinks = true,
}: IProps) => {
  const [showAll, setShowAll] = useState(false);

  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();

  const showMoreButtonVisible =
    (isMobileScreenWidthOrThinner && places.length > SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE) ||
    (isTabletScreenWidthOrThinner && places.length > SHOW_NUMBER_OF_NEARBY_PLACES_TABLET);

  const handleShowMoreClick = () => {
    setShowAll(true);
  };

  return (
    <Container
      id={id}
      className="mb-md scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
    >
      <div className="bg-lake-fog flex flex-col items-start justify-center gap-6 rounded-lg p-6 sm:p-8 md:p-10">
        <Heading as="h2" variant="sm" className="mb-0">
          {title}
        </Heading>
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {places.map(({ name, distance, distanceUnit, region, country, slug }, index) => {
            const placeUrl =
              country.id && region.id && slug
                ? getPlaceUrl(country.id, region.id, slug)
                : undefined;

            if (!name || !placeUrl) {
              return <></>;
            }
            const isHidden =
              (isMobileScreenWidthOrThinner && index >= SHOW_NUMBER_OF_NEARBY_PLACES_MOBILE) ||
              (isTabletScreenWidthOrThinner && index >= SHOW_NUMBER_OF_NEARBY_PLACES_TABLET);

            return (
              <PlaceEntry
                key={name}
                country={country}
                region={region}
                distance={distance}
                distanceUnit={distanceUnit}
                placeName={name}
                placeUrl={placeUrl}
                isHidden={!showAll && isHidden}
                showLocationLinks={showLocationLinks}
              />
            );
          })}
        </div>
        {showMoreButtonVisible && !showAll && (
          <Button
            size="sm"
            className="mt-2 w-full p-2"
            variant="textPrimary"
            onClick={handleShowMoreClick}
          >
            Show more cities
          </Button>
        )}
      </div>
    </Container>
  );
};
